import {
  IconButton,
  Spacer,
  Split,
  SplitSection,
  SvgMenu,
  useMedia,
} from '@loomhq/lens';
import cn from 'classnames';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { EXP_INVITE_STORAGE_INCENTIVES_ROLLOUT } from 'constants/featureFlag';
import { STORAGE_INCENTIVES_VARIATIONS_ROLLOUT } from 'constants/incentives';
// Remove when notifications moves to sidebar
import { selectInTrimMode } from 'selectors/shareVideo';
import { selectUserIsLogged } from 'selectors/user';

import useDowntimeBanner from 'hooks/useDowntimeBanner';
import useTheme from 'hooks/useTheme';
import { useFlagIsActivated } from 'hooks/featureFlag';

import { AnonymousHeader } from './anonymous-header';

import SearchBar from 'components/destination-search/search-bar';
import DownloadProgress from 'components/download-progress';
import HelpBubble from 'components/help-bubble';
import HideDuringDowntime from 'components/hide-during-downtime';
import HeaderPortalDestination from 'components/layout/header/portal-destination';
import { useNavigationStore } from 'components/layout/navigation/navigation-store';
import { BubbleMenu, ProfileBubb } from 'components/profile-bubble';
import UpgradePrompt from 'components/profile-bubble/upgrade-prompt';
import { useShouldShowWaveform } from 'components/share-video/edit-tools-old/hooks';

import styles from './styles.module.less';

type HeaderV2Props = {
  setNavBarIsOpenMobile: () => void;
  isShareVideo: false;
};

export const HeaderV2 = ({
  setNavBarIsOpenMobile,
  isShareVideo,
}: HeaderV2Props): JSX.Element => {
  const [menu, setMenu] = useState<string | null>(null);
  const isUserLoggedIn = useSelector(selectUserIsLogged);
  const isInTrimMode = useSelector(selectInTrimMode);
  const { isCollapsed } = useNavigationStore();

  useDowntimeBanner();
  useTheme();

  const shouldShowWaveform = useShouldShowWaveform();

  const onBubbleMenuExit = useCallback(() => {
    setTimeout(() => {
      document.body.classList.remove('noscroll');
      setMenu(null);
    }, 150);
  }, [setMenu]);

  const onBubbleMenuOpen = () => setMenu('bubb');

  // Website Storage Incentives
  const showVideoLimitsInHeader = useFlagIsActivated({
    flag: EXP_INVITE_STORAGE_INCENTIVES_ROLLOUT,
    activationValues: STORAGE_INCENTIVES_VARIATIONS_ROLLOUT,
  });
  const isMobile = useMedia(['(max-width: 767px)'], [true], false);

  // Header for non-logged in users
  if (!isUserLoggedIn) {
    return <AnonymousHeader setNavBarIsOpenMobile={setNavBarIsOpenMobile} />;
  }

  const isBubb = menu === 'bubb';

  const headerBar = (
    <>
      {isInTrimMode && shouldShowWaveform && (
        <div
          className={cn(
            styles.headerOverlay,
            'absolute top:0 left:0 width:full height:full bgc:overlay'
          )}
        ></div>
      )}
      <DownloadProgress />
      <ul className={cn(styles.headerBar, 'items:center')}>
        <li className={cn(styles.headerBarItem)}>
          <div className={cn(styles.mobileMenu, 'mr:auto')}>
            <Spacer right="small">
              <IconButton
                altText="Toggle library navigation"
                icon={<SvgMenu />}
                onClick={() => setNavBarIsOpenMobile()}
              />
            </Spacer>
          </div>
        </li>
        <li className={cn(styles.searchBar, styles.headerBarItem, 'py:medium')}>
          <SearchBar />
        </li>
        <li className={cn(styles.headerBarItem, 'py:medium items:center flex')}>
          <HeaderPortalDestination />
        </li>
        {showVideoLimitsInHeader && !isMobile && (
          <li className={cn(styles.headerBarItem)}>
            <UpgradePrompt minimal />
          </li>
        )}
      </ul>
    </>
  );

  return (
    <>
      <header
        className={cn(
          styles.header,
          isShareVideo ? styles.shareVideoHeader : styles.destinationHeader,
          'sticky top:0 bgc:background'
        )}
      >
        {isShareVideo ? (
          <div className={cn(styles.headerDisplay)}>
            <div
              className={cn(
                styles.headerWidth,
                isCollapsed ? styles.collapsedSidebar : styles.expandedSidebar
              )}
            >
              {headerBar}
            </div>
          </div>
        ) : (
          <div>{headerBar}</div>
        )}
        <div className={cn(styles.profileBubble)}>
          <div
            className={
              isCollapsed
                ? styles.bubbleBorderCollapsed
                : styles.bubbleBorderExpanded
            }
          />
          <HideDuringDowntime>
            <div className="p:medium">
              <Split gap="medium" wrap="nowrap">
                <SplitSection>
                  <ul className={cn(styles.actions, 'flex items:center')}>
                    <li>
                      <ProfileBubb onOpen={onBubbleMenuOpen} />
                    </li>
                  </ul>
                </SplitSection>
              </Split>
              {isBubb && <BubbleMenu onExit={onBubbleMenuExit} />}
              {isUserLoggedIn && <HelpBubble />}
            </div>
          </HideDuringDowntime>
        </div>
      </header>
    </>
  );
};
